import React, { useState, useEffect } from 'react';
import { colors } from '../../../Colors';
import { clipAudioApi, aiModelsApi } from '../../../services/api';
import ImagesTable from '../UtilsComponents/ImagesTable';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Grid,
  Chip,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Image, 
  Movie,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

function Step3ImageGeneration({ projectId, projectData, onImagesGenerated, onNextStep, onRefreshProject }) {
  const [imagePrompt, setImagePrompt] = useState('');
  const [imagesPerScene, setImagesPerScene] = useState(2);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showScenes, setShowScenes] = useState(false);
  
  // AI Models state
  const [imageGenerationModels, setImageGenerationModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);

  // Fetch AI models for image generation
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        const models = await aiModelsApi.getAIModels('image_generation_models');
        
        // Ensure models is always an array
        const modelsArray = Array.isArray(models) ? models : [];
        setImageGenerationModels(modelsArray);
        
        // Set the first model as default if available
        if (modelsArray.length > 0) {
          setSelectedModel(modelsArray[0].id);
        }
      } catch (err) {
        console.error('Error fetching AI models:', err);
        setImageGenerationModels([]); // Set empty array on error
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  // Handle image download
  const handleDownloadImage = async (imageUrl, sceneNumber, imageIndex) => {
    try {
      // Method 1: Try direct download first
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `scene-${sceneNumber}-image-${imageIndex + 1}.jpg`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Direct download failed, trying fetch method:', error);
      
      try {
        // Method 2: Fallback to fetch with CORS handling
        const response = await fetch(imageUrl, {
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `scene-${sceneNumber}-image-${imageIndex + 1}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (fetchError) {
        console.error('Fetch method also failed:', fetchError);
        
        // Method 3: Final fallback - open in new tab
        window.open(imageUrl, '_blank');
      }
    }
  };


  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      // Include model selection in stepData
      const stepData = selectedModel ? { model: selectedModel } : {};
      const result = await clipAudioApi.executeStep(projectId, projectData.current_step, stepData);
      console.log('Generation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
    } catch (error) {
      console.error('Error generating images:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    
    try {
      const result = await clipAudioApi.validateStep(projectId, projectData.current_step);
      console.log('Validation successful:', result);
      // Refresh project data to get latest state
      if (onRefreshProject) {
        onRefreshProject();
      }
      // Handle successful validation
      if (onNextStep) {
        onNextStep();
      }
    } catch (error) {
      console.error('Error validating step:', error);
    } finally {
      setIsValidating(false);
    }
  };



  const scenes = projectData?.scenes?.scenes || [];

  return (
    <Box sx={{ p: 2.5 }}>


      {/* Scenes Reference */}
      <Box sx={{ 
        bgcolor: '#F8F9FA', 
        borderRadius: 2, 
        p: 2,
        mb: 2.5,
        border: '1px solid #E1E8ED'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
              Étape 3: Génération d'Images
            </Typography>
            {/* Theme Display */}
            {(() => {
              const theme = projectData?.lyric?.theme || projectData?.theme;
              const themeText = typeof theme === 'string' ? theme : (theme?.theme || '');
              return themeText && (
                <Typography variant="body1" sx={{ color: '#2C3E50', fontWeight: 'bold' }}>
                  Thème: {themeText}
                </Typography>
              );
            })()}
          </Box>
          {scenes && scenes.length > 0 && (
            <Button
              onClick={() => setShowScenes(!showScenes)}
              startIcon={showScenes ? <ExpandLess /> : <ExpandMore />}
              variant="outlined"
              size="small"
              sx={{
                borderColor: '#5A6C7D',
                color: '#5A6C7D',
                '&:hover': {
                  borderColor: '#4A5B6C',
                  backgroundColor: '#F5F7FA'
                }
              }}
            >
              {showScenes ? 'Masquer' : 'Voir scènes'}
            </Button>
          )}
        </Box>
        
        {/* Scenes Display */}
        {scenes && scenes.length > 0 && showScenes && (
          <Box sx={{ 
            bgcolor: '#F5F7FA', 
            borderRadius: 1, 
            p: 1.5,
            mt: 1.5,
            border: '1px solid #5A6C7D'
          }}>
            <Typography variant="body2" sx={{ 
              color: '#2C3E50',
              fontWeight: 'bold',
              mb: 1
            }}>
              {scenes.length} scènes disponibles:
            </Typography>
            {scenes.map((scene, index) => (
              <Box key={index} sx={{ mb: 1, pl: 1 }}>
                <Typography variant="body2" sx={{ 
                  color: '#7F8C8D',
                  fontSize: '0.85rem',
                  lineHeight: 1.3
                }}>
                  <strong>Scène {scene.number}:</strong> {scene.title}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#7F8C8D',
                  fontSize: '0.75rem',
                  fontStyle: 'italic'
                }}>
                  {scene.one_line}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        
        {scenes && scenes.length > 0 ? (
          <Typography variant="body2" sx={{ 
            color: '#7F8C8D',
            fontStyle: 'italic',
            mt: 1
          }}>
            "Créons maintenant des images visuelles pour chaque scène..."
          </Typography>
        ) : (
          <Box sx={{ 
            bgcolor: '#FFEBEE', 
            borderRadius: 2, 
            p: 2.5,
            border: '2px solid #EF5350',
            mt: 1
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              color: '#C62828',
              mb: 1
            }}>
              ⚠️ Aucune scène disponible
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#C62828',
              fontStyle: 'italic'
            }}>
              Veuillez d'abord générer des scènes à l'étape 2.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Image Generation Form */}
             <Card sx={{ mb: 2.5, borderRadius: 3, bgcolor: '#F8F9FA' }}>
        <CardContent sx={{ p: 3 }}>
                     <Typography variant="h4" component="h3" sx={{ 
             textAlign: 'center', 
             mb: 2, 
             fontWeight: 'bold',
             color: '#2C3E50',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             gap: 1
           }}>
            <Image color="secondary" />
            Génération d'Images
          </Typography>
          
          {/* Model Selection */}
          <Box sx={{ mb: 2.5, p: 1.5, bgcolor: '#F8F9FA', borderRadius: 1, border: '1px solid #E0E0E0' }}>
            {modelsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Chargement des modèles...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '80px', fontSize: '0.75rem' }}>
                  🤖 Modèle pour la génération des images:
                </Typography>
                <Box sx={{ minWidth: '150px', maxWidth: '200px' }}>
                  <Select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      height: '28px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498DB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2980B9',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2980B9',
                      },
                      '& .MuiSelect-select': {
                        padding: '4px 8px',
                        fontSize: '0.8rem',
                      },
                    }}
                  >
                    {imageGenerationModels.map((model) => (
                      <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                          {model.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}
          </Box>
          
          <Box>
            <TextField
              fullWidth
              label="Décrivez le style des images à générer"
              placeholder="Ex: Images avec des couleurs vibrantes, style cinématographique, éclairage dramatique..."
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              multiline
              rows={3}
              variant="outlined"
              sx={{ mb: 2.5 }}
            />

            

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                             <Button
                 variant="contained"
                 onClick={handleGenerate}
                 disabled={isGenerating}
                 startIcon={isGenerating ? <CircularProgress size={20} /> : <Image />}
                sx={{
                  background: 'linear-gradient(135deg, #5A6C7D 0%, #4A5B6C 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  minWidth: '150px',
                  boxShadow: '0 8px 25px rgba(90, 108, 125, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4A5B6C 0%, #3A4B5C 100%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 30px rgba(90, 108, 125, 0.6)'
                  },
                   '&:disabled': {
                     background: '#BDBDBD',
                     transform: 'none',
                     boxShadow: 'none'
                   }
                 }}
              >
                {isGenerating ? 'Génération...' : 'Générer'}
              </Button>
              
                             <Button
                 variant="contained"
                 onClick={handleValidate}
                 disabled={isValidating || projectData?.step_status !== "completed"}
                 startIcon={isValidating ? <CircularProgress size={20} /> : <Image />}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  minWidth: '150px',
                  boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 30px rgba(39, 174, 96, 0.6)'
                  },
                   '&:disabled': {
                     background: '#BDBDBD',
                     transform: 'none',
                     boxShadow: 'none'
                   }
                 }}
              >
                {isValidating ? 'Validation...' : 'Valider'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Display Scenes with Images when step is completed */}
      {projectData?.step_status === "completed" && projectData?.images && projectData.images.length > 0 && (
        <Card sx={{ mt: 2.5, borderRadius: 3, bgcolor: '#F3E5F5' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" component="h4" sx={{ 
              textAlign: 'center', 
              mb: 2, 
              fontWeight: 'bold',
              color: '#2C3E50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <Image color="secondary" />
              Images générées ({projectData.images.length} scènes)
            </Typography>
            
            {/* Use ImagesTable component */}
            <ImagesTable 
              images={projectData.images}
              projectId={projectId}
              onProjectUpdate={onRefreshProject}
              selectedModel={selectedModel}
              modelsLoading={modelsLoading}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => handleValidate()}
                startIcon={<Image />}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  minWidth: '180px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)'
                  }
                }}
              >
                Continuer vers l'Étape 4
              </Button>
            </Box>
          </CardContent>
        </Card>
             )}


     </Box>
   );
 }

export default Step3ImageGeneration;
